import React, { useEffect, useRef, useState } from "react";
import { Smile, Camera, Brain, Workflow, BarChart3 } from "lucide-react";
import SectionHeading from "./SectionHeading";

const STEPS = [
  {
    icon: Smile,
    title: "Welcome",
    desc: "Guest walks up. Stori greets them, personalizes the experience from the first frame.",
  },
  {
    icon: Camera,
    title: "Engage",
    desc: "AI generates branded content on-the-fly. Guest captures the moment — and wants to share it.",
  },
  {
    icon: Brain,
    title: "Analyze",
    desc: "AI detects demographics, social reach, influencer status, and guest type in real-time.",
  },
  {
    icon: Workflow,
    title: "Trigger",
    desc: "The right marketing playbook activates — VIP, family, business traveler, or standard guest.",
  },
  {
    icon: BarChart3,
    title: "Measure",
    desc: "Follow-up campaigns fire automatically. ROI is tracked, attributed, and reported.",
  },
];

export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  const goToStep = (i) => {
    const el = refs.current[i];
    if (!el) return;
    // Snap state immediately so the visual reflects the click before scroll lands.
    setActive(i);
    // Compute exact target position to vertically center the card.
    const rect = el.getBoundingClientRect();
    const targetTop = window.scrollY + rect.top - (window.innerHeight / 2 - rect.height / 2);
    window.scrollTo({ top: targetTop, behavior: "smooth" });
    // Drop focus so we don't render a phantom focus ring on top of the active border.
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  useEffect(() => {
    const compute = () => {
      const middle = window.innerHeight / 2;
      let nearest = 0;
      let minDist = Infinity;
      refs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - middle);
        if (dist < minDist) {
          minDist = dist;
          nearest = i;
        }
      });
      setActive((prev) => (prev === nearest ? prev : nearest));
    };

    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        compute();
        raf = null;
      });
    };

    compute(); // initial
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="HOW IT WORKS · 5-STEP LOOP"
          title="Capture, classify, trigger — on autopilot."
          lead="Every guest interaction follows the same intelligent loop. No two follow-ups are the same."
        />

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          {/* Left: sticky visual */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 p-7 lg:p-9 rounded-2xl border border-stori-border bg-gradient-to-br from-stori-surface1 to-stori-surface2 min-h-[520px] flex flex-col overflow-hidden relative">
              {/* Background grid + ambient glow that follows the step */}
              <div className="absolute inset-0 grid-noise opacity-25 pointer-events-none" />
              <div className="absolute -top-16 -right-16 w-72 h-72 bg-stori-cyan/12 rounded-full blur-[100px] pointer-events-none" />

              {/* Big animated icon visual */}
              <div className="relative my-8 lg:my-10 flex items-center justify-center" style={{ minHeight: "200px" }}>
                {/* Concentric rings */}
                <div className="absolute w-56 h-56 rounded-full border border-stori-cyan/10" />
                <div className="absolute w-44 h-44 rounded-full border border-stori-cyan/20" />
                <div className="absolute w-32 h-32 rounded-full border border-stori-cyan/40 pulse-ring" />
                <div className="absolute w-32 h-32 rounded-full bg-stori-cyan/10 blur-2xl" />
                {/* Active icon — re-mounts on step change so the animation re-fires */}
                <div
                  key={active}
                  className="relative w-24 h-24 rounded-2xl bg-stori-bg border-2 border-stori-cyan flex items-center justify-center cyan-glow fade-up"
                >
                  {(() => {
                    const ActiveIcon = STEPS[active].icon;
                    return <ActiveIcon className="w-12 h-12 text-stori-cyan" />;
                  })()}
                </div>
              </div>

              {/* Step name + description */}
              <div className="relative">
                <div className="font-heading text-3xl lg:text-4xl text-white font-semibold tracking-tight">
                  {STEPS[active].title}
                </div>
                <p className="mt-3 text-stori-muted leading-relaxed max-w-md">
                  {STEPS[active].desc}
                </p>
              </div>

              {/* Mini flow visual at the bottom */}
              <div className="relative mt-auto pt-8">
                <div className="flex items-center justify-between gap-1">
                  {STEPS.map((s, i) => {
                    const Icon = s.icon;
                    const isActive = i === active;
                    const isCompleted = i < active;
                    return (
                      <React.Fragment key={i}>
                        <button
                          type="button"
                          onClick={() => goToStep(i)}
                          aria-label={`Go to step ${i + 1}: ${s.title}`}
                          data-testid={`mini-flow-step-${i + 1}`}
                          className={`flex flex-col items-center gap-1.5 transition-opacity cursor-pointer hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-stori-cyan/60 rounded ${
                            isActive ? "opacity-100" : isCompleted ? "opacity-70" : "opacity-40"
                          }`}
                        >
                          <div
                            className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                              isActive
                                ? "border-2 border-stori-cyan bg-stori-cyan/10 text-stori-cyan scale-110"
                                : isCompleted
                                ? "border border-stori-cyan/50 text-stori-cyan/80"
                                : "border border-stori-border text-stori-muted"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {isActive && (
                              <span className="absolute inset-0 rounded-full pulse-ring" />
                            )}
                          </div>
                          <span
                            className={`font-mono text-[8px] uppercase tracking-[0.18em] whitespace-nowrap ${
                              isActive ? "text-stori-cyan" : "text-stori-muted/70"
                            }`}
                          >
                            {s.title}
                          </span>
                        </button>
                        {i < STEPS.length - 1 && (
                          <div
                            className={`flex-1 h-px transition-colors ${
                              i < active ? "bg-stori-cyan/60" : "bg-stori-border"
                            }`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right: scrolling steps */}
          <div className="lg:col-span-7 space-y-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = active === i;
              return (
                <button
                  type="button"
                  key={i}
                  ref={(el) => (refs.current[i] = el)}
                  data-idx={i}
                  data-testid={`how-step-${i + 1}`}
                  onClick={() => goToStep(i)}
                  aria-label={`View step ${i + 1}: ${s.title}`}
                  className={`group w-full text-left p-7 rounded-xl border transition-all duration-300 cursor-pointer focus:outline-none ${
                    isActive
                      ? "border-stori-cyan/60 bg-stori-surface1 cyan-glow"
                      : "border-stori-border bg-stori-bg/50 hover:border-stori-cyan/40 hover:bg-stori-surface1/40"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center border transition-colors ${
                        isActive
                          ? "border-stori-cyan text-stori-cyan bg-stori-cyan/5"
                          : "border-stori-border text-stori-muted"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-stori-muted">
                          STEP {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`h-px flex-1 ${
                            isActive ? "bg-stori-cyan" : "bg-stori-border"
                          }`}
                        />
                      </div>
                      <h3 className="mt-2 font-heading text-2xl font-semibold text-white">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-stori-muted leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
